import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, ArrowRight, CalendarClock, ClipboardCheck, HelpCircle, FileText, X } from 'lucide-react';

const PERFIS = ['fisioterapeuta', 'fonoaudiologo', 'nutricionista', 'psicologo', 'medico'];
const ESP_DO_PERFIL = { fisioterapeuta: 'fisio', fonoaudiologo: 'fono', nutricionista: 'nutri', psicologo: 'psicologo', medico: 'clinico' };
const LABEL = { fisio: 'Fisioterapia', fono: 'Fonoaudiologia', nutri: 'Nutrição', psicologo: 'Psicologia', clinico: 'Clínica Médica' };
const fmt = (d) => (d ? d.split('-').reverse().join('/') : '');

const classificar = (linhas, esp, userId) => {
  const pend = linhas.flatMap((l) => l.pendencias.map((p) => ({ ...p, paciente_nome: l.paciente_nome, numero_prontuario: l.numero_prontuario, data_admissao: l.data_admissao, internacao_id: l.internacao_id })))
    .filter((p) => p.especialidade === esp || p.profissional_id === userId);
  return {
    agendar: pend.filter((p) => ['aguardando_atribuicao', 'aguardando_agendamento'].includes(p.status)),
    agendadas: pend.filter((p) => p.status === 'agendado'),
    decidir: pend.filter((p) => p.status === 'aguardando_decisao'),
    guias: pend.filter((p) => p.guia && !p.guia.lancada),
    atrasadas: pend.filter((p) => p.atrasado && ['aguardando_atribuicao', 'aguardando_agendamento', 'agendado'].includes(p.status)),
  };
};

const Grupo = ({ icone: Icone, titulo, itens, cor, testid, children }) => itens.length > 0 && (
  <div className={`rounded-lg border p-3 ${cor}`} data-testid={testid}>
    <div className="flex items-center gap-2 font-medium text-sm"><Icone className="h-4 w-4" />{titulo}<Badge variant="secondary" className="ml-auto">{itens.length}</Badge></div>
    {children && <p className="text-xs mt-1 opacity-80">{children}</p>}
    <ul className="mt-2 space-y-1 text-xs max-h-28 overflow-y-auto">
      {itens.slice(0, 6).map((p) => <li key={p.id} className="flex flex-wrap gap-x-2"><span className="font-medium">{p.paciente_nome}</span><span className="opacity-70">#{p.numero_prontuario || '—'} · admissão {fmt(p.data_admissao)}{p.status === 'agendado' && p.agendamento ? ` · agendada ${fmt(p.agendamento.data_prevista)} ${p.agendamento.horario_previsto || ''}` : ''}</span></li>)}
      {itens.length > 6 && <li className="opacity-70">+ {itens.length - 6} paciente(s)</li>}
    </ul>
  </div>
);

export const AvisoPrimeirasAvaliacoes = () => {
  const { api, user, can } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [linhas, setLinhas] = useState(null);
  const [aberto, setAberto] = useState(false);
  const perfil = (user?.perfil || '').toLowerCase();
  const esp = ESP_DO_PERFIL[perfil];
  const chave = user ? `pa_aviso_visto_${user.id}` : null;
  const elegivel = !!esp && PERFIS.includes(perfil) && (can('ver_primeiros_atendimentos') || can('ver_primeiros_atendimentos_todos'));

  useEffect(() => {
    if (!elegivel) return;
    let ativo = true;
    api.get('/primeiros-atendimentos/painel').then((r) => { if (ativo) setLinhas(r.data?.linhas || []); }).catch(() => { if (ativo) setLinhas([]); });
    return () => { ativo = false; };
  }, [api, elegivel, pathname]);

  const g = useMemo(() => (linhas ? classificar(linhas, esp, user?.id) : null), [linhas, esp, user]);
  const total = g ? g.agendar.length + g.agendadas.length + g.decidir.length + g.guias.length : 0;

  useEffect(() => {
    if (g && total > 0 && chave && !sessionStorage.getItem(chave) && !pathname.startsWith('/primeiros-atendimentos')) setAberto(true);
  }, [g, total, chave, pathname]);

  if (!elegivel || !g || total === 0) return null;
  const fechar = () => { sessionStorage.setItem(chave, '1'); setAberto(false); };
  const abrirPainel = () => { fechar(); navigate(`/primeiros-atendimentos?especialidade=${esp}`); };
  const urgente = g.atrasadas.length > 0 || g.decidir.length > 0;

  return (
    <>
      {!pathname.startsWith('/primeiros-atendimentos') && (
        <div className={`mx-6 mt-4 flex flex-wrap items-center gap-3 rounded-md border px-4 py-2.5 text-sm ${urgente ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-amber-300 bg-amber-50 text-amber-900'}`} data-testid="pa-aviso-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1"><b>Primeiras Avaliações — {LABEL[esp]}:</b> {total} pendência(s)
            {g.agendar.length > 0 && <> · {g.agendar.length} a agendar</>}{g.agendadas.length > 0 && <> · {g.agendadas.length} agendada(s)</>}{g.decidir.length > 0 && <> · {g.decidir.length} aguardando sua decisão de acompanhamento</>}{g.guias.length > 0 && <> · {g.guias.length} guia(s) pendente(s)</>}
          </span>
          <Button size="sm" variant="outline" onClick={() => setAberto(true)} data-testid="pa-aviso-banner-detalhes">Ver detalhes</Button>
          <Button size="sm" onClick={abrirPainel} data-testid="pa-aviso-banner-abrir">Abrir painel <ArrowRight className="h-4 w-4 ml-1" /></Button>
        </div>)}
      <Dialog open={aberto} onOpenChange={(o) => { if (!o) fechar(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pa-aviso-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className={`h-5 w-5 ${urgente ? 'text-rose-600' : 'text-amber-600'}`} />Pendências de Primeiras Avaliações — {LABEL[esp]}</DialogTitle>
            <DialogDescription>Olá, {user?.nome?.split(' ')[0]}. Há {total} pendência(s) de primeira avaliação da sua especialidade. Este aviso será exibido a cada acesso até que sejam resolvidas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Grupo icone={HelpCircle} titulo="Aguardando sua decisão de acompanhamento" itens={g.decidir} cor="border-violet-300 bg-violet-50 text-violet-900" testid="pa-aviso-decidir">Avaliação realizada: informe se o paciente tem ou não necessidade de acompanhamento contínuo da especialidade.</Grupo>
            <Grupo icone={CalendarClock} titulo="Aguardando agendamento da primeira avaliação" itens={g.agendar} cor={g.atrasadas.length ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-amber-300 bg-amber-50 text-amber-900'} testid="pa-aviso-agendar">{g.atrasadas.length ? `${g.atrasadas.length} em atraso conforme prazo configurado.` : 'Pacientes admitidos disponíveis para avaliação inicial.'}</Grupo>
            <Grupo icone={ClipboardCheck} titulo="Avaliações agendadas — confirmar realização" itens={g.agendadas} cor="border-sky-300 bg-sky-50 text-sky-900" testid="pa-aviso-agendadas" />
            <Grupo icone={FileText} titulo="Guias da primeira avaliação não lançadas" itens={g.guias} cor="border-orange-300 bg-orange-50 text-orange-900" testid="pa-aviso-guias">Lance a guia no Controle de Guias.</Grupo>
          </div>
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={fechar} data-testid="pa-aviso-depois"><X className="h-4 w-4 mr-1" />Lembrar no próximo acesso</Button>
            <Button onClick={abrirPainel} data-testid="pa-aviso-abrir">Abrir Primeiras Avaliações <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
